import type { IModifierQuantityObj } from '@/app/[locale]/_store/types'
import type { ISavedModifier } from '@/app/[locale]/_types'
import { resolveLocaleField } from '../resolve-locale-field'

function isCompulsoryModifierSatisfied(
  compulsory: ISavedModifier,
  orderModifiers: ISavedModifier[],
  modifiersQuantity: IModifierQuantityObj[]
): boolean {
  const hasParent = !!compulsory.parent_id

  return orderModifiers.some((element) => {
    const parentMatches = hasParent
      ? element.parent_id === compulsory.parent_id
      : !element.parent_id && element.modifier_id === compulsory.modifier_id

    const quantityValid = element.modifier_quantity > 0

    const minAmountValid = hasParent
      ? (modifiersQuantity.find((mod) => mod.id === element.parent_id)
          ?.quantity || 0) >= (element.parent_min_amount || 0)
      : true

    return parentMatches && quantityValid && minAmountValid
  })
}

/** Returns true when every compulsory modifier requirement is met. */
export function validateRequiredModifiers(
  compulsories: ISavedModifier[],
  orderModifiers: ISavedModifier[],
  modifiersQuantity: IModifierQuantityObj[]
): boolean {
  if (!compulsories?.length) return true

  return compulsories.every((item) =>
    isCompulsoryModifierSatisfied(item, orderModifiers, modifiersQuantity)
  )
}

/** Returns localized display names for compulsory modifiers that are not satisfied. */
export function getMissingRequiredModifierNames(
  compulsories: ISavedModifier[],
  orderModifiers: ISavedModifier[],
  modifiersQuantity: IModifierQuantityObj[],
  locale: string
): string[] {
  if (!compulsories?.length) return []

  const modifierNames: Record<string, string> = {}

  for (const item of compulsories) {
    if (isCompulsoryModifierSatisfied(item, orderModifiers, modifiersQuantity)) {
      continue
    }

    const hasParent = !!item.parent_id
    const field = hasParent ? item.parent_name : item.name
    const name = resolveLocaleField(field as Record<string, string>, locale)

    if (name) {
      modifierNames[name] = name
    }
  }

  return Object.values(modifierNames)
}
